import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as L from 'leaflet';

export interface HoverZoneInfo {
  id: string;
  label: string;
  description?: string;
  minAltitude?: number;
  maxAltitude?: number;
  color: string;
}

interface RegisteredShape {
  id: string;
  type: 'circle' | 'polygon';
  layer: L.Circle | L.Polygon;
  info: HoverZoneInfo;
}

/**
 * Provided at the LeafletMapComponent level so each map instance is isolated.
 *
 * Instead of per-shape mouseover/mouseout events (which fail with Canvas renderer
 * when shapes overlap), this service stores references to every drawn shape and
 * does a geometric point-in-shape test on every map mousemove event. This means
 * ALL overlapping zones appear in the panel simultaneously.
 */
@Injectable()
export class MapHoverService {
  private readonly zonesSubject = new BehaviorSubject<HoverZoneInfo[]>([]);
  readonly zones$ = this.zonesSubject.asObservable();

  private shapes: RegisteredShape[] = [];
  private counter = 0;

  registerCircle(circle: L.Circle, info: Omit<HoverZoneInfo, 'id'>): void {
    const id = `hs-${++this.counter}`;
    this.shapes.push({ id, layer: circle, type: 'circle', info: { ...info, id } });
    circle.on('remove', () => this.unregisterLayer(circle));
  }

  registerPolygon(polygon: L.Polygon, info: Omit<HoverZoneInfo, 'id'>): void {
    const id = `hs-${++this.counter}`;
    this.shapes.push({ id, layer: polygon, type: 'polygon', info: { ...info, id } });
    polygon.on('remove', () => this.unregisterLayer(polygon));
  }

  /** Called on every map mousemove — updates the visible zones to all shapes under the cursor.
   * Emits only when the set of zones actually changes to avoid redundant renders. */
  updateFromLatLng(latlng: L.LatLng): void {
    const hit = this.shapes.filter(s => this.containsPoint(latlng, s));
    const current = this.zonesSubject.getValue();
    // Compare by IDs — insertion order matches the shapes array order so join is stable.
    if (hit.map(s => s.id).join(',') !== current.map(z => z.id).join(',')) {
      this.zonesSubject.next(hit.map(s => s.info));
    }
  }

  /** Remove all registered shapes (call when clearing infra layers). */
  clearShapes(): void {
    this.shapes = [];
    this.zonesSubject.next([]);
  }

  private unregisterLayer(layer: L.Circle | L.Polygon): void {
    const removedIds = new Set(
      this.shapes.filter((shape) => shape.layer === layer).map((shape) => shape.id)
    );

    if (removedIds.size === 0) {
      return;
    }

    this.shapes = this.shapes.filter((shape) => shape.layer !== layer);
    this.zonesSubject.next(
      this.zonesSubject.getValue().filter((zone) => !removedIds.has(zone.id))
    );
  }

  private containsPoint(latlng: L.LatLng, shape: RegisteredShape): boolean {
    try {
      if (shape.type === 'circle') {
        const c = shape.layer as L.Circle;
        return latlng.distanceTo(c.getLatLng()) <= c.getRadius();
      }
      return this.pointInPolygon(latlng, shape.layer as L.Polygon);
    } catch {
      return false;
    }
  }

  /** Ray-casting algorithm for point-in-polygon in geographic coordinates. */
  private pointInPolygon(latlng: L.LatLng, polygon: L.Polygon): boolean {
    const raw = polygon.getLatLngs();
    if (!raw?.length) { return false; }
    // getLatLngs() returns L.LatLng[][] for polygons with holes; outer ring is [0]
    const ring = (Array.isArray(raw[0]) ? raw[0] : raw) as L.LatLng[];
    const x = latlng.lng;
    const y = latlng.lat;
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i].lng, yi = ring[i].lat;
      const xj = ring[j].lng, yj = ring[j].lat;
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }
}
